import { formatBytes } from '../../../utils/format';

/** `$29` for whole dollars, `$21.75` otherwise. Null in → null out. */
export function formatPrice(cents) {
  if (cents == null) return null;
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** Display price for a billing cycle: `{amount, unit}`; custom plans → `Custom`. */
export function planPrice(plan, cycle) {
  if (plan.isCustomPricing) {
    return { amount: 'Custom', unit: null };
  }
  const cents =
    cycle === 'annual' ? (plan.priceAnnualCents ?? plan.priceMonthlyCents) : plan.priceMonthlyCents;
  if (cents == null) {
    return { amount: 'Custom', unit: null };
  }
  return { amount: formatPrice(cents), unit: '/mo' };
}

/** The small line under the price: seats · workspaces · storage (+ seat overage). */
export function planPriceSub(plan, cycle) {
  if (plan.isCustomPricing) {
    return plan.limits?.priceSub ?? 'Custom limits';
  }
  const parts = [];
  if (plan.adminSeatsIncluded != null) {
    parts.push(`${plan.adminSeatsIncluded} seat${plan.adminSeatsIncluded === 1 ? '' : 's'}`);
  }
  parts.push(
    plan.workspaceLimit == null
      ? 'unlimited workspaces'
      : `${plan.workspaceLimit} active workspaces`,
  );
  const bytes = Number(plan.storageQuotaBytes);
  if (bytes > 0) {
    // "1.0 TB" → "1 TB" to match the marketing copy.
    parts.push(formatBytes(bytes).replace(/\.0 /, ' '));
  }
  let sub = parts.join(' · ');
  if (plan.extraSeatPriceCents != null) {
    sub += ` · +${formatPrice(plan.extraSeatPriceCents)}/mo per extra seat`;
  }
  if (plan.extraSeatPriceCents != null && plan.maxSeats != null) sub += ` (max ${plan.maxSeats})`;
  if (cycle === 'annual' && plan.priceAnnualCents != null) {
    sub += ' · billed annually';
  }
  return sub;
}

/**
 * The amount invoiced per billing period, in cents — mirrors the backend's
 * classification formula (annual plans store an effective monthly price and
 * bill 12× it once a year).
 */
export function periodPriceCents(plan, cycle) {
  return cycle === 'annual'
    ? (plan?.priceAnnualCents ?? plan?.priceMonthlyCents ?? 0) * 12
    : (plan?.priceMonthlyCents ?? 0);
}

/**
 * 'same' | 'upgrade' | 'downgrade' — how the backend will treat this change.
 * Upgrades are charged prorated immediately; downgrades wait for period end.
 */
export function classifyPlanChange(currentPlan, currentCycle, targetPlan, targetCycle) {
  if (currentPlan?.id === targetPlan?.id && currentCycle === targetCycle) return 'same';
  return periodPriceCents(targetPlan, targetCycle) > periodPriceCents(currentPlan, currentCycle)
    ? 'upgrade'
    : 'downgrade';
}

/** Discount implied by the seeded prices (e.g. 25); null when not derivable. */
export function annualSavingsPercent(plans) {
  const sample = (plans ?? []).find(
    (plan) => plan.priceMonthlyCents > 0 && plan.priceAnnualCents != null,
  );
  if (!sample) return null;
  return Math.round((1 - sample.priceAnnualCents / sample.priceMonthlyCents) * 100);
}

/** Module allowances come from effective plan fields, never stale marketing strings. */
export function planFeatures(plan) {
  const sections = plan.features ?? [];
  if (!['starter', 'professional', 'business', 'enterprise'].includes(plan.slug)) return sections;
  const sign = plan.signingDocumentLimit == null ? 'unlimited' : `${plan.signingDocumentLimit.toLocaleString('en-US')} signature requests per month`;
  const flow = plan.formSubmissionLimit == null ? 'unlimited' : `${plan.formSubmissionLimit.toLocaleString('en-US')}/month`;
  return [{ heading: 'Included modules', items: [
    { text: 'MyDocUploads Core' }, { text: 'MyDocUploads Conversations' },
    { text: 'MyDocUploads Secure Drop — unlimited' },
    { text: `MyDocUploads Sign — ${sign}` }, { text: `MyDocUploads Flow — ${flow}` },
  ] }, ...sections.filter(s => !['Included modules', 'Available add-ons'].includes(s.heading)).map(s => ({
    ...s, items: (s.items ?? []).filter(i => i.text !== 'All modules'),
  }))];
}
